//====== WR data 03/09/2026
// Motivo da alteração: porte do SmartFlow Importer — tela "Parceiros de Negócio Importados"
// (portal: page-specific-components/smartflow/ClientesImportadosTable.js +
// ClientesImportadosDetails.js). Decisão do usuário: acessada POR PROJETO, na sidebar logo abaixo
// da entrada "SmartFlow Importer". Lista/busca/pagina a staging do projeto (MongoDB via
// smartflow-backend) e permite excluir dos 2 lados (Mongo + SAP — o tenant valida CNPJ único
// entre Parceiros, um registro "preso" impede reimportar).
//
// Rota: /admin/smartFlowParceirosImportados/:parentId (parentId = id do SmartFlowProject).
// Faz login na Service Layer com as credenciais do projeto (igual ProjectConsole/SmartFlowImporter).
//======
import React from "react"
import ReactBSAlert from "react-bootstrap-sweetalert"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Input,
  Button,
  Badge,
  Modal,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap"

import { utils, writeFile } from "xlsx"

import SimpleHeader from "components/Headers/SimpleHeader.js"
import Loading from "components/Modals/Loading"
import api from "services/api"
import apiSap from "services/sapApi"
import {
  listarParceirosStaging,
  excluirParceiroStaging,
} from "services/smartflow/importerApi"
import { excluirParceiroDoSap } from "services/smartflow/smartflowSapWrite"
import { formatCnpjCpf } from "components/SmartFlow/importerLogic"

const USEREMAIL_KEY = "smartflow_user_email"

function formatarData(valor) {
  if (!valor) return "—"
  try {
    return new Date(valor).toLocaleString("pt-BR")
  } catch (error) {
    return "—"
  }
}

function tipoPn(cardType) {
  if (cardType === "cSupplier") return { label: "Fornecedor", color: "success" }
  if (cardType === "cCustomer") return { label: "Cliente", color: "info" }
  return { label: cardType || "—", color: "secondary" }
}

function enderecoResumido(p) {
  const logr = [p.tipoLogradouro, p.enderecoStreet].filter(Boolean).join(" ")
  return (
    [
      [logr, p.enderecoStreetNo].filter(Boolean).join(", "),
      p.enderecoComplemento,
      p.enderecoBlock,
      p.enderecoCity && p.enderecoCounty ? `${p.enderecoCity}/${p.enderecoCounty}` : p.enderecoCity,
      p.enderecoZipCode,
    ]
      .filter(Boolean)
      .join(" — ") || "—"
  )
}

class SmartFlowParceirosImportados extends React.Component {
  state = {
    loading: true,
    project: null,
    alert: null,

    termo: "",
    page: 1,
    parceiros: [],
    total: 0,
    pageSize: 20,
    listLoading: false,
    listError: null,
    excluindoId: null,
    exportando: false,

    detalhe: null,
  }

  _debounce = null

  componentDidMount() {
    this.loadProject(this.props.match.params.parentId)
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.match.params.parentId !== this.state.project?._id) {
      this.loadProject(nextProps.match.params.parentId)
    }
  }

  loadProject = async (id) => {
    this.setState({ loading: true })
    try {
      const { data: project } = await api.get("/smartflowproject/read/" + id)
      if (project.serviceLayerUrl) {
        try {
          document.cookie = "B1SESSION=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"
          const loginResp = await apiSap.post(project.serviceLayerUrl + "/Login", {
            CompanyDB: project.serviceLayerDataBase,
            UserName: project.serviceLayerUser,
            Password: project.serviceLayerPassword,
          })
          localStorage.setItem("sl_session_id", loginResp.data.SessionId)
        } catch (error) {
          localStorage.removeItem("sl_session_id")
          this.notify(
            "Não foi possível logar na Service Layer do projeto. A exclusão no SAP pode falhar.",
            "warning"
          )
        }
      }
      this.setState({ project, loading: false }, this.fetchLista)
    } catch (error) {
      this.notify("Não foi possível carregar o projeto.", "danger")
      this.setState({ loading: false })
    }
  }

  fetchLista = async () => {
    const { project, termo, page } = this.state
    if (!project) return
    this.setState({ listLoading: true, listError: null })
    try {
      const { parceiros, total, pageSize } = await listarParceirosStaging({
        projectId: project._id,
        termo,
        page,
      })
      this.setState({ parceiros, total, pageSize: pageSize || 20, listLoading: false })
    } catch (error) {
      this.setState({
        listLoading: false,
        listError: error?.response?.data?.error || error?.message || "Falha ao carregar a lista.",
      })
    }
  }

  //====== WR data 16/09/2026 — pedido do usuário: exportar Excel também nesta tela de consulta
  // (mesma ideia da tela de Itens Importados). Busca TODAS as páginas que batem com o filtro
  // atual, não só a página visível.
  handleExportarExcel = async () => {
    const { project, termo } = this.state
    if (!project) return
    this.setState({ exportando: true })
    try {
      const todos = []
      let page = 1
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { parceiros, total } = await listarParceirosStaging({ projectId: project._id, termo, page })
        todos.push(...parceiros)
        if (todos.length >= total || !parceiros.length) break
        page += 1
      }
      if (!todos.length) {
        this.notify("Nenhum parceiro pra exportar.", "warning")
        return
      }
      const linhas = todos.map((p) => ({
        CardCode: p.cardCode,
        Tipo: tipoPn(p.cardType).label,
        "Razão social": p.cardName,
        "Nome fantasia": p.cardForeignName,
        "CNPJ/CPF": formatCnpjCpf(p.federalTaxId, p.taxIdEstrangeiro),
        "Inscrição Estadual": p.inscricaoEstadual,
        "E-mail": p.email,
        Telefone: p.telefone,
        Endereço: enderecoResumido(p),
        País: p.enderecoCountry,
        Situação: p.valid === "tYES" ? "Ativo" : "Inativo",
        Status: p.status === "gravado_sap" ? "Gravado no SAP" : "Simulado",
        "Nota de origem": [p.origemNumeroNf, p.origemArquivoNome].filter(Boolean).join(" — "),
        "Preparado por": p.preparadoPor,
        "Preparado em": formatarData(p.preparadoEm),
      }))
      const planilha = utils.json_to_sheet(linhas)
      const livro = utils.book_new()
      utils.book_append_sheet(livro, planilha, "Parceiros Importados")
      writeFile(livro, `smartflow-parceiros-importados-${project._id}.xlsx`)
    } catch (error) {
      this.notify(error?.response?.data?.error || error?.message || "Falha ao exportar.", "danger")
    } finally {
      this.setState({ exportando: false })
    }
  }

  onTermoChange = (e) => {
    const termo = e.target.value
    this.setState({ termo, page: 1 })
    clearTimeout(this._debounce)
    this._debounce = setTimeout(this.fetchLista, 400)
  }

  goToPage = (page) => this.setState({ page }, this.fetchLista)

  handleExcluir = async (parceiro) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Excluir o parceiro "${parceiro.cardName || parceiro.cardCode}"? Remove do SAP (se existir) e ` +
        `do banco. Esta ação não pode ser desfeita.`
    )
    if (!ok) return

    //====== WR data 09/09/2026
    // Motivo da alteração: bug reportado (print "Network Error") — excluía do SAP mas a chamada ao
    // backend (excluir do espelho) caía no cold start do Render, deixando pela metade. Agora as
    // duas exclusões são independentes: tenta as duas, junta o que falhou, e só reporta sucesso
    // total se as duas passaram. `excluirParceiroStaging` já tem retry (importerApi.comRetry).
    // ======
    const slUrl = this.state.project?.serviceLayerUrl
    this.setState({ excluindoId: parceiro._id })
    const falhas = []
    try {
      if (slUrl) await excluirParceiroDoSap(parceiro.cardCode, slUrl)
    } catch (error) {
      falhas.push(`SAP: ${error?.message || "falha"}`)
    }
    try {
      await excluirParceiroStaging(parceiro, localStorage.getItem(USEREMAIL_KEY) || null)
    } catch (error) {
      falhas.push(`banco interno: ${error?.message || "falha"}`)
    }

    this.setState({ excluindoId: null })
    if (falhas.length === 0) {
      this.notify("Parceiro excluído com sucesso (SAP + banco interno).", "success")
      this.setState({ detalhe: null }, this.fetchLista)
    } else {
      this.notify(
        "Exclusão parcial — " +
          falhas.join(" | ") +
          ". Tente 'Excluir' de novo em alguns segundos (o backend pode estar acordando).",
        "danger"
      )
      this.fetchLista()
    }
  }

  notify = (message, type = "info") => {
    this.setState({
      alert: (
        <ReactBSAlert
          {...(type === "success" ? { success: true } : type === "danger" ? { danger: true } : { warning: true })}
          style={{ display: "block", marginTop: "-100px" }}
          title=""
          onConfirm={() => this.setState({ alert: null })}
          onCancel={() => this.setState({ alert: null })}
          confirmBtnBsStyle={type === "danger" ? "danger" : type === "success" ? "success" : "warning"}
          confirmBtnText="Ok"
          btnSize=""
        >
          {message}
        </ReactBSAlert>
      ),
    })
  }

  renderPaginacao() {
    const { page, total, pageSize } = this.state
    const nPages = Math.max(1, Math.ceil(total / pageSize))
    if (nPages <= 1) return null
    const paginas = []
    for (let p = 1; p <= nPages; p++) {
      if (p === 1 || p === nPages || Math.abs(p - page) <= 2) paginas.push(p)
      else if (paginas[paginas.length - 1] !== "…") paginas.push("…")
    }
    return (
      <Pagination className="pagination justify-content-end mt-3" listClassName="justify-content-end">
        <PaginationItem disabled={page <= 1}>
          <PaginationLink onClick={() => this.goToPage(page - 1)}>‹</PaginationLink>
        </PaginationItem>
        {paginas.map((p, i) =>
          p === "…" ? (
            <PaginationItem disabled key={`e${i}`}>
              <PaginationLink>…</PaginationLink>
            </PaginationItem>
          ) : (
            <PaginationItem active={p === page} key={p}>
              <PaginationLink onClick={() => this.goToPage(p)}>{p}</PaginationLink>
            </PaginationItem>
          )
        )}
        <PaginationItem disabled={page >= nPages}>
          <PaginationLink onClick={() => this.goToPage(page + 1)}>›</PaginationLink>
        </PaginationItem>
      </Pagination>
    )
  }

  renderDetalhe() {
    const p = this.state.detalhe
    if (!p) return null
    //====== WR data 09/09/2026 — endereço longo (sem hífens/espaços que quebrem) vazava a largura
    // do modal. tableLayout fixed + wordBreak nas células força a quebra dentro da caixa.
    const linha = (k, v) => (
      <tr>
        <td
          className="text-muted align-top"
          style={{ width: 170, wordBreak: "break-word" }}
        >
          {k}
        </td>
        <td style={{ wordBreak: "break-word", whiteSpace: "normal" }}>{v || "—"}</td>
      </tr>
    )
    return (
      <Modal className="modal-dialog-centered" size="lg" isOpen={!!p}>
        <div className="modal-header">
          <h5 className="modal-title" style={{ wordBreak: "break-word" }}>
            {p.cardName || p.cardCode}{" "}
            <Badge color={tipoPn(p.cardType).color}>{tipoPn(p.cardType).label}</Badge>
          </h5>
          <button className="close" onClick={() => this.setState({ detalhe: null })}>
            <span>×</span>
          </button>
        </div>
        <div className="modal-body" style={{ overflowWrap: "anywhere" }}>
          <Table size="sm" borderless style={{ tableLayout: "fixed", width: "100%" }}>
            <tbody>
              {linha("CardCode", p.cardCode)}
              {linha("Razão social", p.cardName)}
              {linha("Nome fantasia", p.cardForeignName)}
              {linha("CNPJ/CPF", formatCnpjCpf(p.federalTaxId, p.taxIdEstrangeiro))}
              {linha("Inscrição Estadual", p.inscricaoEstadual)}
              {linha("E-mail", p.email)}
              {linha("Telefone", p.telefone)}
              {linha("Grupo (code)", p.groupCode)}
              {linha("Endereço", enderecoResumido(p))}
              {linha("País", p.enderecoCountry)}
              {linha("Cód. município IBGE", p.codigoMunicipioIbge)}
              {linha("Situação", p.valid === "tYES" ? "Ativo" : "Inativo")}
              {linha(
                "Status",
                p.status === "gravado_sap" ? "Gravado no SAP" : "Simulado (não gravado)"
              )}
              {linha(
                "Nota de origem",
                [p.origemNumeroNf, p.origemArquivoNome].filter(Boolean).join(" — ")
              )}
              {linha("Preparado por", p.preparadoPor)}
              {linha("Preparado em", formatarData(p.preparadoEm))}
              {linha("Gravado em", formatarData(p.gravadoEm))}
            </tbody>
          </Table>
        </div>
        <div className="modal-footer">
          <Button
            color="danger"
            disabled={this.state.excluindoId === p._id}
            onClick={() => this.handleExcluir(p)}
          >
            {this.state.excluindoId === p._id ? "Excluindo..." : "Excluir"}
          </Button>
          <Button color="secondary" onClick={() => this.setState({ detalhe: null })}>
            Fechar
          </Button>
        </div>
      </Modal>
    )
  }

  render() {
    const { loading, project, alert, parceiros, listLoading, listError, termo, excluindoId, exportando } =
      this.state

    return (
      <>
        {alert}
        {this.renderDetalhe()}
        <SimpleHeader
          name="Parceiros de Negócio Importados"
          items={[
            { level: "nav", name: project ? project.name : "Projeto" },
            { level: "nav", name: "Parceiros Importados" },
          ]}
        />
        <Container className="mt--6" fluid>
          <Row>
            <Col xl="12">
              {loading || !project ? (
                <Loading modalOpened={loading} />
              ) : (
                <Card>
                  <CardHeader className="d-flex align-items-start justify-content-between">
                    <div>
                      <h5 className="h3 mb-0">Parceiros de Negócio Importados</h5>
                      <Input
                        type="text"
                        bsSize="sm"
                        className="mt-2"
                        style={{ maxWidth: 420 }}
                        placeholder="Buscar (CardCode, razão social, nome fantasia, CNPJ/CPF)"
                        value={termo}
                        onChange={this.onTermoChange}
                      />
                    </div>
                    {/*====== WR data 16/09/2026 — pedido do usuário: exportar Excel também nesta
                    tela de consulta. */}
                    <Button color="secondary" size="sm" onClick={this.handleExportarExcel} disabled={exportando}>
                      {exportando ? "Exportando..." : "Gerar Excel"}
                    </Button>
                  </CardHeader>
                  <CardBody>
                    {listError && <div className="alert alert-danger">{listError}</div>}
                    {listLoading ? (
                      <p className="text-muted small">Carregando...</p>
                    ) : parceiros.length === 0 ? (
                      <p className="text-muted small">
                        {termo
                          ? "Nenhum parceiro encontrado para essa busca."
                          : 'Nenhum parceiro importado ainda. Prepare e grave parceiros na aba "Validação SAP" do SmartFlow Importer.'}
                      </p>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <Table className="align-items-center table-flush" responsive hover>
                            <thead className="thead-light">
                              <tr>
                                <th>CardCode</th>
                                <th>Tipo</th>
                                <th>Razão social</th>
                                <th>CNPJ/CPF</th>
                                <th>Endereço</th>
                                <th>Situação</th>
                                <th>Status</th>
                                <th>Origem</th>
                                <th>Preparado em</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {parceiros.map((p) => (
                                <tr
                                  key={p._id}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => this.setState({ detalhe: p })}
                                >
                                  <td>{p.cardCode}</td>
                                  <td>
                                    <Badge color={tipoPn(p.cardType).color}>
                                      {tipoPn(p.cardType).label}
                                    </Badge>
                                  </td>
                                  <td>{p.cardName || "—"}</td>
                                  <td>{formatCnpjCpf(p.federalTaxId, p.taxIdEstrangeiro)}</td>
                                  <td className="small">{enderecoResumido(p)}</td>
                                  <td>
                                    <Badge color={p.valid === "tYES" ? "success" : "danger"}>
                                      {p.valid === "tYES" ? "Ativo" : "Inativo"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge color={p.status === "gravado_sap" ? "success" : "warning"}>
                                      {p.status === "gravado_sap" ? "Gravado no SAP" : "Simulado"}
                                    </Badge>
                                  </td>
                                  <td className="small">
                                    {[p.origemNumeroNf, p.origemArquivoNome].filter(Boolean).join(" — ") ||
                                      "—"}
                                  </td>
                                  <td className="small">{formatarData(p.preparadoEm)}</td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      disabled={excluindoId === p._id}
                                      onClick={() => this.handleExcluir(p)}
                                    >
                                      {excluindoId === p._id ? "..." : "Excluir"}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                        {this.renderPaginacao()}
                      </>
                    )}
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </Container>
      </>
    )
  }
}

export default SmartFlowParceirosImportados
